import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/pro-regular-svg-icons/faBell";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons/faChevronLeft";
import { useTranslation } from "react-i18next";
import { useNotificationsUnread } from "../../hooks/visitor/useNotificationsUnread";
import type { VisitorNotification } from "./notification-types";
import { ShareButton } from "./ShareButton";

interface VisitorPageHeaderProps {
  logoUrl?: string;
  appName?: string;
  /** Show share button. Caller must pass shareUrl/shareTitle when true. */
  showShare?: boolean;
  shareUrl?: string;
  shareTitle?: string;
  shareText?: string;
  /** Sub-page mode: replaces logo with back button + centered title */
  showBack?: boolean;
  onBack?: () => void;
  title?: string;
  notifications: VisitorNotification[];
  onBellClick: () => void;
  isTrayOpen: boolean;
  mapSlug: string;
  brandAccent?: string | null;
}

/**
 * Shared page header for visitor sub-tabs (Search, Events, More).
 * Default: logo + bell (with unread badge) + optional share button.
 * Sub-page mode (`showBack`): back button + centered title + bell.
 *
 * Map tab uses SearchHeader instead — it morphs into a search input.
 */
export function VisitorPageHeader({
  logoUrl,
  appName,
  showShare,
  shareUrl,
  shareTitle,
  shareText,
  showBack,
  onBack,
  title,
  notifications,
  onBellClick,
  isTrayOpen,
  mapSlug,
  brandAccent,
}: VisitorPageHeaderProps) {
  const { t } = useTranslation("map");
  const unreadCount = useNotificationsUnread(notifications, mapSlug);

  return (
    <div className="relative flex items-center justify-between px-4 h-14 pt-safe">
      {/* Left: back button OR logo */}
      <div className="flex items-center gap-2 min-w-0">
        {showBack ? (
          <button
            type="button"
            onClick={onBack}
            className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-muted transition-colors -ml-2 outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t("nav.back")}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4 text-foreground" aria-hidden />
          </button>
        ) : logoUrl ? (
          <img
            src={logoUrl}
            alt={appName ?? "Map"}
            className="h-8 w-auto max-w-[140px] object-contain"
          />
        ) : appName ? (
          <span className="font-semibold text-sm truncate">{appName}</span>
        ) : null}
      </div>

      {/* Center: title (sub-page only) */}
      {showBack && title && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 font-semibold text-sm truncate max-w-[55%] pt-safe"
          aria-hidden={false}
        >
          {title}
        </div>
      )}

      {/* Right: actions */}
      <div className="flex items-center gap-1 shrink-0">
        {showShare && (
          <ShareButton
            url={shareUrl}
            title={shareTitle}
            text={shareText}
            brandAccent={brandAccent}
            className="w-11 h-11"
          />
        )}
        <button
          type="button"
          onClick={onBellClick}
          aria-expanded={isTrayOpen}
          aria-controls="notifications-tray"
          aria-haspopup="dialog"
          className="relative w-11 h-11 flex items-center justify-center rounded-full hover:bg-muted transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t("nav.notifications")}
        >
          <FontAwesomeIcon
            icon={faBell}
            className="h-4 w-4 text-foreground"
            style={brandAccent ? { color: brandAccent } : undefined}
            aria-hidden
          />
          {unreadCount > 0 && (
            <span
              className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500 border border-background"
              aria-hidden
            />
          )}
        </button>
      </div>
    </div>
  );
}
